import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import clsx from "clsx";
import { Tooltip } from "react-tooltip";

import { QUERY_WIDTHS, LENGTH_USER_NAME_FOR_HEADER } from "@utils/index";
import { useCommonStore, useUserStore } from "@stores/index";
import { useNotificationGetAll } from "@providers/index";
import {
  ArrowUpLeftSvg,
  HelpSquareRoundedSvg,
  BellRingingSvg,
  UserSquareRoundedSvg,
  DotsVerticalSvg,
  UserSvg,
  LogoutSvg,
  SearchBar,
  NotificationsBar,
  UpdateBanner,
} from "@components/index";
import LogoImg from "@assets/images/logo.svg";

export const Header = () => {
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });
  const isTablet = useMediaQuery({ query: QUERY_WIDTHS.lg });
  const isAuth = useCommonStore(({ isAuth }) => isAuth);
  const clientCode = useUserStore(({ clientCode }) => clientCode);
  const userFullName = useUserStore(({ userFullName }) => userFullName);

  const [isMenuShown, setIsMenuShown] = useState(false);
  const [isNotificationsShown, setIsNotificationsShown] = useState(false);

  const { data: notifications = [] } = useNotificationGetAll(isAuth);

  const countNotifications = useMemo(() => {
    return notifications.filter(({ readAt }) => !readAt).length;
  }, [notifications]);

  return (
    <>
      <nav className="flex h-20 justify-between px-4 md:px-8">
        <div className="flex items-center">
          <Link to="/" className="w-40 shrink-0 md:w-[200px]">
            <img
              src={LogoImg}
              alt="Logo"
              width={200}
              className="relative -left-6"
            />
          </Link>
          {isMobile && (
            <Link
              to="https://onvio.co.uk/"
              target="_blank"
              className="flex text-button text-white-54"
            >
              <ArrowUpLeftSvg />
              Onvio
            </Link>
          )}
        </div>
        {!isMobile && (
          <div className="flex items-center gap-x-6">
            <Link
              to="https://onvio.co.uk/"
              target="_blank"
              className="flex gap-x-2 text-button text-white-54 hover:underline"
            >
              <ArrowUpLeftSvg />
              Onvio
            </Link>
            <SearchBar />
          </div>
        )}
        <div className="flex items-center gap-x-4 md:gap-x-5">
          <Link
            to="/faq"
            className="text-white-100"
            data-tooltip-id="tooltip-faq-button"
            data-tooltip-content="FAQ"
            data-tooltip-variant="light"
          >
            <HelpSquareRoundedSvg />
          </Link>
          <Tooltip id="tooltip-faq-button" />
          <div
            className="relative cursor-pointer text-white-100"
            data-tooltip-id="tooltip-notifications-button"
            data-tooltip-content="Notifications"
            data-tooltip-variant="light"
            onClick={() => setIsNotificationsShown(!isNotificationsShown)}
          >
            <BellRingingSvg />
            {countNotifications > 0 && (
              <div className="absolute -right-[7px] -top-[7px] h-4 w-[18px] rounded-full bg-primary-200 text-center text-100 font-bold text-magenta-200">
                {countNotifications > 9 ? "9+" : countNotifications}
              </div>
            )}
          </div>
          <Tooltip id="tooltip-notifications-button" />
          {isTablet ? (
            <Link
              to="/profile"
              className="rounded-lg bg-white-16 p-3 text-white-100"
            >
              <UserSquareRoundedSvg />
            </Link>
          ) : (
            <>
              <div className="h-full w-px bg-white-8" />
              <div className="w-24 text-white-100">
                <div className="font-bold">
                  {userFullName.length > LENGTH_USER_NAME_FOR_HEADER
                    ? userFullName.substring(0, LENGTH_USER_NAME_FOR_HEADER) +
                      "..."
                    : userFullName}
                </div>
                <div className="pt-1 font-light">{clientCode}</div>
              </div>
              <div
                className="relative"
                onMouseEnter={() => setIsMenuShown(true)}
                onMouseLeave={() => setIsMenuShown(false)}
              >
                <div className="cursor-pointer text-white-54">
                  <DotsVerticalSvg />
                </div>
                <ul
                  className={clsx(
                    "absolute -right-[30px] w-[180px] overflow-hidden rounded-lg bg-white-100 shadow-sm transition-all duration-300",
                    isMenuShown ? "h-[112px] py-2" : "h-0 py-0",
                  )}
                >
                  <Link
                    to="/profile"
                    className="flex h-12 items-center gap-x-3 px-6 font-bold text-black-100 *:text-black-54 hover:underline"
                  >
                    <UserSvg />
                    My profile
                  </Link>
                  <Link
                    to="logout"
                    className="flex h-12 items-center gap-x-3 px-6 font-bold text-red-100 hover:underline"
                  >
                    <LogoutSvg />
                    Log out
                  </Link>
                </ul>
              </div>
            </>
          )}
        </div>
      </nav>
      {isMobile && <UpdateBanner />}
      {isMobile && <SearchBar />}
      {isNotificationsShown && (
        <NotificationsBar
          notifications={notifications}
          onClose={() => setIsNotificationsShown(false)}
        />
      )}
    </>
  );
};
